/**
 * Shared assignments tab for client + matter detail screens.
 *
 * Thin wrapper around `@lexiun/org-admin`'s `AssignmentList` primitive.
 * Fetches direct assignment rows via the lexserver REST helpers and
 * renders them read-only for D9a — `onDelete`/`onUpdateRole` handlers
 * are deferred until the LexIDE admin UX lands (D10+).
 *
 * ## Cascade composition (Post-F #2 — 2026-04-17)
 *
 * For a **matter** tab, the component also fetches the parent client's
 * direct assignments and composes them as cascade-derived rows marked
 * `source: "inherited"` with `inheritedFrom.scope = "client"`. This
 * mirrors what `CaseAccessService` does server-side when resolving
 * permissions. A client-level assignment for user U cascades to every
 * matter under that client, so Pedro's persona — "Pedro is LEAD on
 * client Claudio" — shows up as an inherited LEAD row on each of
 * Claudio's matters.
 *
 * De-duplication rule: if the same user already has a direct matter-
 * level assignment, we SKIP adding the inherited client-level row. The
 * direct row wins (an explicit override at a lower scope supersedes
 * the inherited role).
 *
 * ## Why client-side composition, not server-side
 *
 * The lexserver `/assignments` routes return direct rows only (Phase B
 * design). Moving the cascade resolver server-side would duplicate the
 * logic that already lives in `CaseAccessService.checkAccess` in
 * lx-kernel, and the LexIDE host only needs a flat display — the read-
 * side doesn't benefit from server-side materialization. When the
 * LexIDE admin UX adds mutation handlers (D10+), direct rows will
 * remain the only write target; inherited rows are read-only by
 * construction.
 */

import { useQuery } from "@tanstack/react-query";
import { AssignmentList } from "@lexiun/org-admin";
import type { AssignmentScope, UserAssignment } from "@lexiun/org-admin";
import {
  fetchClientAssignments,
  fetchMatterAssignments,
} from "@/lib/lexserver-client";

interface AssignmentsTabProps {
  scope: AssignmentScope;
  clientId: string;
  matterId?: string;
  /**
   * Human-readable client name for the `inheritedFrom.scopeName` hint
   * shown on cascade rows. Optional — if omitted, the rows still
   * render with a generic scope indicator but lose the context clue.
   * Pass from `MatterDetailLayout` where `client.name` is already in
   * scope.
   */
  clientName?: string;
}

/**
 * Compose direct + inherited assignment rows for a matter tab.
 *
 * Rules:
 *   - All direct matter rows render as-is (`source: "direct"`).
 *   - For each client row whose `userId` is NOT already present among
 *     the direct matter rows, add an inherited row with
 *     `source: "inherited"` and `inheritedFrom.scope = "client"`.
 *   - Assignment IDs stay stable: inherited rows reuse the client
 *     assignment's `assignmentId` (unique across the org because the
 *     lexserver assignmentRegistry assigns unique ids regardless of
 *     scope).
 *   - `scope` on the inherited row is still `"matter"` — that's the
 *     scope of the tab rendering it. `inheritedFrom` records the
 *     cascade source (client).
 */
export function composeMatterAssignments(
  directMatterRows: readonly UserAssignment[],
  parentClientRows: readonly UserAssignment[],
  clientId: string,
  clientName?: string,
): UserAssignment[] {
  const directUserIds = new Set(directMatterRows.map((row) => row.userId));
  const inherited: UserAssignment[] = parentClientRows
    .filter((row) => !directUserIds.has(row.userId))
    .map((row) => ({
      ...row,
      scope: "matter" as const,
      source: "inherited" as const,
      inheritedFrom: {
        scope: "client" as const,
        scopeId: clientId,
        scopeName: clientName,
      },
    }));
  // Direct rows come first (visual priority); inherited rows below.
  return [...directMatterRows, ...inherited];
}

export function AssignmentsTab({
  scope,
  clientId,
  matterId,
  clientName,
}: AssignmentsTabProps) {
  // Direct rows for the requested scope.
  const directQuery = useQuery<UserAssignment[], Error>({
    queryKey:
      scope === "client"
        ? ["client-assignments", clientId]
        : ["matter-assignments", clientId, matterId],
    queryFn: async () => {
      if (scope === "client") {
        return fetchClientAssignments(clientId);
      }
      if (!matterId) return [];
      return fetchMatterAssignments(clientId, matterId);
    },
    enabled: scope === "client" || Boolean(matterId),
    staleTime: 60_000,
  });

  // Parent client rows — only fetched for the matter tab. Uses the
  // same cache key as the client tab's own fetch, so the two tabs
  // share a single network round-trip when both are mounted.
  const parentClientQuery = useQuery<UserAssignment[], Error>({
    queryKey: ["client-assignments", clientId],
    queryFn: () => fetchClientAssignments(clientId),
    enabled: scope === "matter" && Boolean(matterId),
    staleTime: 60_000,
  });

  const directRows = directQuery.data ?? [];
  const parentRows = parentClientQuery.data ?? [];
  const composed =
    scope === "matter"
      ? composeMatterAssignments(directRows, parentRows, clientId, clientName)
      : directRows;

  // Loading = direct query still loading (parent is best-effort; the
  // tab is usable even if the inherited section is empty while it
  // loads). Errors from either fetch bubble up.
  const isLoading = directQuery.isLoading;
  const error = directQuery.error ?? parentClientQuery.error;

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
      data-testid={`assignments-tab-${scope}`}
    >
      <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
        Assignments
      </h3>
      {error && (
        <div className="mb-3 text-xs text-red-500">
          Failed to load assignments: {error.message}
        </div>
      )}
      <AssignmentList
        scope={scope}
        assignments={composed}
        loading={isLoading}
        emptyMessage={
          scope === "client"
            ? "No direct client-level assignments yet"
            : "No direct or inherited assignments for this matter yet"
        }
      />
    </section>
  );
}
